/**
 * HotelSwitcher — dropdown below the HOCP logo in the hotel shell.
 * ADR-WF-1 v2.1 §25.2 (P0 revision #5: keep it simple).
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronDown, Check, Plus, Settings } from "lucide-react";
import { useHotelContext } from "@/contexts/HotelContext";
import { useAuth } from "@/contexts/AuthContext";

export default function HotelSwitcher() {
  const { ctx, hotels, switchHotel } = useHotelContext();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!ctx || !ctx.hotel_id) return null;
  const isSuperAdmin = user?.role === "super_admin";
  // For a single-hotel user with no switch option, render a static badge.
  const showChevron = isSuperAdmin || (hotels && hotels.length > 1);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="hotel-switcher-toggle"
        onClick={() => showChevron && setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left"
      >
        <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center shrink-0">
          <Building2 className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-white/50">Hotel</div>
          <div className="text-[13px] font-semibold text-white truncate" data-testid="hotel-switcher-current">
            {ctx.hotel_name || "—"}
          </div>
        </div>
        {showChevron && <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      {open && (
        <div
          data-testid="hotel-switcher-dropdown"
          className="absolute z-50 top-full mt-2 left-0 right-0 rounded-xl bg-hocp-navy border border-white/10 shadow-xl overflow-hidden"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-white/45 border-b border-white/10">
            Recent Hotels
          </div>
          <div className="max-h-[300px] overflow-y-auto py-1">
            {hotels.map((h) => (
              <button
                key={h.id}
                data-testid={`hotel-switcher-item-${h.id}`}
                onClick={async () => {
                  setOpen(false);
                  if (h.id !== ctx.hotel_id) await switchHotel(h.id);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-left"
              >
                <Building2 className="w-3.5 h-3.5 text-white/60 shrink-0" />
                <div className="text-[13px] text-white truncate flex-1">{h.name}</div>
                {h.id === ctx.hotel_id && <Check className="w-4 h-4 text-emerald-400" />}
              </button>
            ))}
          </div>
          {isSuperAdmin && (
            <>
              <div className="border-t border-white/10" />
              <button
                data-testid="hotel-switcher-manage"
                onClick={() => { setOpen(false); navigate("/platform/hotels"); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-left text-[13px] text-white/80"
              >
                <Settings className="w-3.5 h-3.5" />
                Manage Hotels
              </button>
              <button
                data-testid="hotel-switcher-create"
                onClick={() => { setOpen(false); navigate("/platform/hotels/new"); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-left text-[13px] text-amber-400 font-medium"
              >
                <Plus className="w-3.5 h-3.5" />
                Create Hotel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
